/* istanbul ignore file  */
import 'core-js/features/promise';
import 'core-js/features/object';
import 'core-js/features/string';
import 'core-js/features/array';
import 'core-js/features/typed-array';

import React from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { MemoryRouter as Router, useHistory } from 'react-router-dom';
import { Provider } from 'react-redux';
import config from 'config';

import logger, { setLevel, setEnable } from 'common/support/Log';
import focusManager from 'support/focus/FocusManager';
import { initVegaDeviceBridge } from 'support/vegaDeviceBridge';
import { openExitDialog } from 'actions/exitDialog';

import App from './App';
import store from './store';

import './index.css';
import 'bootstrap/dist/css/bootstrap-grid.min.css';
import './utilities.css';

const isReactNativeBridgeEnabled =
  !!import.meta.env.VITE_BRIDGE_REACT_NATIVE &&
  (import.meta.env.VITE_BRIDGE_REACT_NATIVE && import.meta.env.VITE_BRIDGE_REACT_NATIVE !== '0');
const getInitialLocation = () => {
  const { pathname, search, hash } = window.location;
  const isFilePath = /\.[a-z0-9]+$/i.test(pathname);
  return (isFilePath ? '/' : pathname) + search + hash;
};

// MemoryRouter never updates window.location, so bridge route changes out to
// the RN shell (Vega webview) where they surface in device logs for e2e.
const RouteBridge = () => {
  const history = useHistory();
  React.useEffect(() => {
    if (!window.ReactNativeWebView) return undefined;
    const post = (pathname: string) => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'route',
        data: pathname,
      }));
    };
    post(history.location.pathname);
    const unlisten = history.listen(location => post(location.pathname));

    // Voice/deep links forwarded by the RN shell navigate the MemoryRouter.
    const onMessage = (e: MessageEvent) => {
      try {
        const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (msg?.type === 'vega:route' && typeof msg.path === 'string') {
          history.push(msg.path);
        }
      } catch {
        // Ignore malformed messages from the RN shell.
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      unlisten();
      window.removeEventListener('message', onMessage);
    };
  }, [history]);
  return null;
};

const render = (Component: React.ComponentType) => {
  ReactDOM.render(
    <Provider store={store}>
      <Router initialEntries={[getInitialLocation()]}>
        {isReactNativeBridgeEnabled && <RouteBridge />}
        <Component />
      </Router>
    </Provider>,
    document.getElementById('root'),
  );
};

setLevel(config.log.minLevel);
setEnable(config.log.enabled);
logger.addListener((msg, type) => {
  store.dispatch({
    type: 'LOG',
    payload: {
      message: msg,
    },
    metadata: {
      logType: type,
    },
  });
});

render(App);

Modal.setAppElement('#root');

if (isReactNativeBridgeEnabled) {
  if (window.__VEGA_DEBUG__) {
    window.addEventListener('keydown', (e) => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'keypress',
        data: `native keydown: key=${e.key} keyCode=${e.keyCode} code=${e.code}`,
      }));
    }, true);

    // Expose the singleton so the RN shell can query focus state on demand
    // (e.g. e2e asserting initial focus after load).
    window.__focusManager = focusManager;
  }

  if (config.platformName === 'vega') {
    initVegaDeviceBridge(store);
  }

  // Bridge focus changes out to the RN shell (no-op outside the Vega webview).
  if (window.ReactNativeWebView) {
    focusManager.addFocusChangedListener((newId, prevId) => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'focus',
        data: { newId, prevId },
      }));
    });
  }

  window.addEventListener('message', (e) => {
    try {
      const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (msg?.type === 'vega:openExitDialog') {
        store.dispatch(openExitDialog());
        return;
      }
      if (msg?.type !== 'vega:nav') return;
      const keyCode: Record<string, number> = {
        up: 38, down: 40, left: 37, right: 39, select: 13, back: 27,
      };
      const code = keyCode[msg.direction];
      if (code) {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {keyCode: code, which: code, bubbles: true, cancelable: true})
        );
      }
    } catch {
      // Ignore malformed messages from the RN shell.
    }
  });
}
